import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Button,
  Input,
  Card,
} from '../../components';
import { useApiKey } from '../../hooks';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { configure, isConfigured, loading } = useApiKey();
  const [apiKey, setApiKey] = useState('');
  const [storeId, setStoreId] = useState('');
  const [storeName, setStoreName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showSaved, setShowSaved] = useState(false);

  // If already configured, redirect to dashboard
  React.useEffect(() => {
    if (isConfigured && !loading) {
      navigate('/admin', { replace: true });
    }
  }, [isConfigured, loading, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!apiKey.trim()) {
      setError('API Key é obrigatória');
      return;
    }

    if (!storeId.trim()) {
      setError('ID da loja é obrigatório');
      return;
    }

    try {
      configure(apiKey.trim(), storeId.trim(), storeName.trim() || undefined);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError('Falha ao salvar configuração');
    }
  };

  const handleUseSaved = () => {
    setShowSaved(true);
    // The useApiKey hook already loads from localStorage
    // Just navigate if configured
    if (isConfigured) {
      navigate('/admin', { replace: true });
    }
  };

  // Demo keys for development
  const demoKeys = [
    { key: 'loja_teste_123', storeId: 'loja-001', storeName: 'Loja Teste 1' },
    { key: 'loja_b_456', storeId: 'loja-002', storeName: 'Loja B (cross-tenant)' },
  ];

  return (
    <Page
      className="min-h-screen flex items-center justify-center px-4"
      appBar={false}
    >
      <Card variant="elevated" padding="xl" className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-primary text-4xl">admin_panel_settings</span>
          </div>
          <h1 className="font-display-lg-mobile">Painel Admin</h1>
          <p className="font-body-md text-on-surface-variant mt-2">
            Configure sua loja para acessar o painel de vistorias
          </p>
        </div>

        {isConfigured && !showSaved && (
          <div className="mb-6 p-3 rounded-lg bg-success-bg">
            <div className="flex items-center gap-2 text-success-text">
              <span className="material-symbols-outlined">check_circle</span>
              <span className="font-label-md">Loja já configurada</span>
            </div>
            <Button variant="outline" fullWidth onClick={handleUseSaved} className="mt-2">
              Entrar como <span className="font-label-md">{storeName || storeId}</span>
            </Button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-error-container" role="alert">
              <p className="font-body-sm text-on-error-container">{error}</p>
            </div>
          )}

          <Input
            label="API Key"
            placeholder="loja_teste_123"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            type="password"
            autoComplete="off"
            error={error === 'API Key é obrigatória' ? error : undefined}
          />

          <Input
            label="ID da Loja"
            placeholder="loja-001"
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            autoComplete="off"
            error={error === 'ID da loja é obrigatório' ? error : undefined}
          />

          <Input
            label="Nome da Loja (opcional)"
            placeholder="Minha Loja"
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            autoComplete="off"
          />

          <Button variant="primary" fullWidth type="submit" disabled={loading} isLoading={loading}>
            Entrar no Painel
          </Button>
        </form>

        {import.meta.env.DEV && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-outline-variant" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-surface-container-lowest text-on-surface-variant">Modo Desenvolvimento</span>
              </div>
            </div>

            <div className="space-y-2">
              {demoKeys.map((demo) => (
                <Button
                  key={demo.storeId}
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    setApiKey(demo.key);
                    setStoreId(demo.storeId);
                    setStoreName(demo.storeName);
                  }}
                >
                  <span className="material-symbols-outlined">science</span>
                  {demo.storeName} ({demo.storeId})
                </Button>
              ))}
            </div>
          </>
        )}

        <p className="font-body-sm text-on-surface-variant text-center mt-6">
          A API Key é fornecida pelo administrador do sistema Car-IA.
        </p>
      </Card>
    </Page>
  );
};